import { create, StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface ProjectItem {
  id: string;
  projectId: string;
}

export interface ProjectItemState<T extends ProjectItem> {
  /** 所有專案的資料（各頁依 projectId 篩選；persist 讓重新整理時先顯示快取） */
  items: T[];
  /** 換掉某個專案的全部資料（GET /projects/:id 帶回的子資料、單獨重抓） */
  setProjectItems: (projectId: string, items: T[]) => void;
  add: (item: T) => void;
  update: (id: string, updates: Partial<T>) => void;
  remove: (id: string) => void;
  /** 先從畫面移除再呼叫 API，失敗回滾並把錯誤丟回給呼叫端提示 */
  optimisticDelete: (id: string, deleteFn: (id: string) => Promise<unknown>) => Promise<void>;
}

export type ProjectItemStore<T extends ProjectItem> = UseBoundStore<StoreApi<ProjectItemState<T>>>;

/**
 * 專案底下的子資料 store（需求、分包、里程碑、任務、收款共用；原本 5 份幾乎相同的 store）
 * @param path 列表 API 路徑 /projects/:id/<path>（回應是陣列或 { <path>: 陣列 }）
 */
export function createProjectItemStore<T extends ProjectItem>(path: string) {
  return create<ProjectItemState<T>>()(
    persist(
      (set, get) => ({
        items: [],
        setProjectItems: (projectId, items) =>
          set(state => ({ items: [...state.items.filter(item => item.projectId !== projectId), ...items] })),
        add: item => set(state => ({ items: [...state.items, item] })),
        update: (id, updates) =>
          set(state => ({ items: state.items.map(item => (item.id === id ? { ...item, ...updates } : item)) })),
        remove: id => set(state => ({ items: state.items.filter(item => item.id !== id) })),
        optimisticDelete: async (id, deleteFn) => {
          const before = get().items;
          get().remove(id);
          try {
            await deleteFn(id);
          } catch (error) {
            set({ items: before });
            throw error;
          }
        },
      }),
      { name: `pm-${path}-storage`, partialize: state => ({ items: state.items }) }
    )
  );
}

/** 重抓某個專案的子資料（看板、甘特圖在別頁改了任務時用） */
export async function refetchProjectItems<T extends ProjectItem>(store: ProjectItemStore<T>, path: string, projectId: string) {
  const { data } = await api.get(`/projects/${projectId}/${path}`);
  store.getState().setProjectItems(projectId, Array.isArray(data) ? data : data[path] ?? []);
}

/**
 * 對話框的樂觀新增／更新（照 CRM）：先寫入 store、對話框立即關閉；API 成功換成伺服器回傳的資料，失敗回滾後把錯誤丟回呼叫端提示
 * @param original 編輯前的資料；沒有＝新增（draft 帶暫時 id temp-…）
 */
export async function saveOptimistic<T extends ProjectItem>(
  store: ProjectItemStore<T>,
  { original, draft, request }: { original?: T; draft: T; request: () => Promise<{ data: T }> }
): Promise<T> {
  const { add, update, remove } = store.getState();
  if (original) update(original.id, draft);
  else add(draft);

  try {
    const { data } = await request();
    update(draft.id, data);
    return data;
  } catch (error) {
    if (original) update(original.id, original);
    else remove(draft.id);
    throw error;
  }
}
